import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { FONT_BODY } from './styles'

// /admin/billing: who owes the $99/month tech fee. Route and admin gate live in
// App.js (BillingView); a non-admin gets an "Admins only" notice there, never
// this screen. agent_billing RLS lets admins read and write every row.
//
// Every profile, left-joined to agent_billing in memory. NO ROW = EXEMPT:
// current agents have no row. Switching to On upserts { user_id, tech_fee:'on' }
// (an existing row keeps its status and PayPal fields); switching to Exempt only
// updates tech_fee. A row is never deleted, so payment history is kept.
// Status and payment dates are written by the PayPal webhook, not here.
//
// Link PayPal subscription: for agents who subscribed before the Hub button
// (no custom_id). It goes through tech-fee-link with target_user_id, which
// reads the subscription from PayPal, checks the plan and that no other agent
// has it, and saves the row with the service role.

const STATUS_LABEL = {
  not_set_up: 'Not set up',
  active: 'Active',
  past_due: 'Past due',
  cancelled: 'Cancelled',
}

const HAIRLINE = '0.5px solid #e8e8e8'
const ROW_FIELDS = 'user_id, tech_fee, status, last_payment_at, paypal_subscription_id'

function formatDay(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric' })
}

export default function BillingAdmin({ showToast }) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])        // profiles, each with .billing (row or null)
  const [savingId, setSavingId] = useState(null)
  const [linkInput, setLinkInput] = useState({})   // user id -> typed subscription id
  const [linkMsg, setLinkMsg] = useState({})       // user id -> { ok, text }
  const [linkingId, setLinkingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [profilesRes, billingRes] = await Promise.all([
        supabase.from('profiles').select('id, full_name, email, brokerage').order('full_name'),
        supabase.from('agent_billing').select(ROW_FIELDS),
      ])
      if (profilesRes.error) {
        console.error('[billing] profiles load:', profilesRes.error)
        showToast('Could not load agents: ' + profilesRes.error.message, true)
        setRows([])
        return
      }
      if (billingRes.error) {
        console.error('[billing] agent_billing load:', billingRes.error)
        showToast('Could not load billing: ' + billingRes.error.message, true)
      }
      const byUser = {}
      ;(billingRes.data || []).forEach(b => { byUser[b.user_id] = b })
      setRows((profilesRes.data || []).map(p => ({ ...p, billing: byUser[p.id] || null })))
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Mount only, as TeamsAdmin does: depending on `load` would re-fire whenever
  // App.js's showToast changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  async function setFee(agent, next) {
    if (savingId) return
    const current = agent.billing?.tech_fee === 'on' ? 'on' : 'exempt'
    if (next === current) return
    setSavingId(agent.id)
    try {
      if (next === 'on') {
        const { data, error } = await supabase.from('agent_billing')
          .upsert({ user_id: agent.id, tech_fee: 'on' }, { onConflict: 'user_id' })
          .select(ROW_FIELDS).single()
        if (error) throw error
        setRows(prev => prev.map(r => (r.id === agent.id ? { ...r, billing: data } : r)))
      } else {
        // No row is already Exempt; nothing to write.
        if (!agent.billing) return
        const { data, error } = await supabase.from('agent_billing')
          .update({ tech_fee: 'exempt' }).eq('user_id', agent.id)
          .select(ROW_FIELDS)
        if (error) throw error
        if (!data || data.length === 0) throw new Error('no row updated')
        setRows(prev => prev.map(r => (r.id === agent.id ? { ...r, billing: data[0] } : r)))
      }
      showToast((agent.full_name || agent.email || 'Agent') + ': tech fee ' + (next === 'on' ? 'on' : 'exempt'))
    } catch (e) {
      console.error('[billing] set tech fee:', e)
      showToast('Could not change the tech fee: ' + (e.message || 'unknown error'), true)
    } finally {
      setSavingId(null)
    }
  }

  async function linkSubscription(agent) {
    const subId = String(linkInput[agent.id] || '').trim().toUpperCase()
    if (!subId || linkingId) return
    setLinkingId(agent.id)
    setLinkMsg(prev => ({ ...prev, [agent.id]: null }))
    let msg = ''
    let row = null
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession()
      const { data, error } = await supabase.functions.invoke('tech-fee-link', {
        body: { subscription_id: subId, target_user_id: agent.id },
        headers: { Authorization: `Bearer ${authSession.access_token}` },
      })
      msg = data?.error || ''
      if (!msg && error) { try { const b = await error.context?.json?.(); msg = b?.error } catch (_) {} msg = msg || error.message }
      row = data?.row || null
    } catch (e) {
      msg = e.message || 'unknown error'
    }
    if (msg || !row) {
      setLinkMsg(prev => ({ ...prev, [agent.id]: { ok: false, text: msg || 'No row came back.' } }))
    } else {
      const saved = { user_id: row.user_id, tech_fee: row.tech_fee, status: row.status, last_payment_at: row.last_payment_at, paypal_subscription_id: row.paypal_subscription_id }
      setRows(prev => prev.map(r => (r.id === agent.id ? { ...r, billing: saved } : r)))
      setLinkInput(prev => ({ ...prev, [agent.id]: '' }))
      setLinkMsg(prev => ({ ...prev, [agent.id]: { ok: true, text: 'Linked: ' + (STATUS_LABEL[row.status] || row.status) } }))
    }
    setLinkingId(null)
  }

  const cell = { padding: '10px 12px', borderBottom: HAIRLINE, fontSize: '13px', color: '#1d2e42', fontFamily: FONT_BODY, verticalAlign: 'middle' }
  const head = { ...cell, fontSize: '12px', fontWeight: 500, color: '#888', textAlign: 'left' }
  const small = { fontSize: '11.5px', color: '#888', marginTop: '3px', wordBreak: 'break-all' }

  if (loading) return <div style={{ padding: '24px 0', fontSize: '13px', color: '#888', fontFamily: FONT_BODY }}>Loading…</div>

  const onCount = rows.filter(r => r.billing?.tech_fee === 'on').length

  return (
    <div style={{ fontFamily: FONT_BODY }}>
      <div style={{ fontSize: '13px', color: '#555', marginBottom: '14px' }}>
        $99/month tech fee. {onCount} of {rows.length} {rows.length === 1 ? 'account is' : 'accounts are'} on. No row means Exempt.
      </div>
      <div style={{ overflowX: 'auto', border: HAIRLINE, borderRadius: '10px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
          <thead>
            <tr>
              <th style={head}>Agent</th>
              <th style={head}>Tech fee</th>
              <th style={head}>Status</th>
              <th style={head}>Last payment</th>
              <th style={head}>Link PayPal subscription</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const fee = r.billing?.tech_fee === 'on' ? 'on' : 'exempt'
              const busy = savingId === r.id
              const linking = linkingId === r.id
              const msg = linkMsg[r.id]
              const typed = linkInput[r.id] || ''
              return (
                <tr key={r.id}>
                  <td style={cell}>
                    <div style={{ fontWeight: 500 }}>{r.full_name || '(no name)'}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{r.email}{r.brokerage ? ' · ' + r.brokerage : ''}</div>
                  </td>
                  <td style={cell}>
                    <div style={{ display: 'inline-flex', border: HAIRLINE, borderRadius: '16px', overflow: 'hidden', opacity: busy ? 0.5 : 1 }}>
                      {[['on', 'On'], ['exempt', 'Exempt']].map(([val, label]) => (
                        <button key={val} type="button" disabled={busy} onClick={() => setFee(r, val)}
                          aria-pressed={fee === val}
                          style={{
                            border: 'none', padding: '5px 12px', fontSize: '12px', fontFamily: FONT_BODY, fontWeight: 500,
                            cursor: busy ? 'default' : 'pointer',
                            background: fee === val ? (val === 'on' ? '#147efb' : '#f4f4f2') : 'transparent',
                            color: fee === val ? (val === 'on' ? '#ffffff' : '#1d2e42') : '#888',
                          }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td style={cell}>
                    {r.billing ? (STATUS_LABEL[r.billing.status] || r.billing.status) : '—'}
                    {r.billing?.paypal_subscription_id && <div style={small}>{r.billing.paypal_subscription_id}</div>}
                    {r.billing?.tech_fee === 'exempt' && r.billing?.status === 'active' && (
                      <div style={{ fontSize: '12px', color: '#c0392b', marginTop: '3px' }}>Exempt but still paying in PayPal. Cancel it there.</div>
                    )}
                  </td>
                  <td style={cell}>{r.billing ? formatDay(r.billing.last_payment_at) : '—'}</td>
                  <td style={cell}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input value={typed} disabled={linking} placeholder="I-XXXXXXXXXXXX"
                        onChange={e => setLinkInput(prev => ({ ...prev, [r.id]: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') linkSubscription(r) }}
                        aria-label={'PayPal subscription id for ' + (r.full_name || r.email || 'agent')}
                        style={{ width: '150px', border: HAIRLINE, borderRadius: '6px', padding: '5px 8px', fontSize: '12px', fontFamily: FONT_BODY, color: '#1d2e42', background: '#ffffff' }} />
                      <button type="button" disabled={linking || !typed.trim()} onClick={() => linkSubscription(r)}
                        style={{ border: HAIRLINE, borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: 500, fontFamily: FONT_BODY, background: '#ffffff',
                          color: linking || !typed.trim() ? '#aaa' : '#147efb', cursor: linking || !typed.trim() ? 'default' : 'pointer' }}>
                        {linking ? 'Linking…' : 'Link'}
                      </button>
                    </div>
                    {msg && <div style={{ fontSize: '12px', marginTop: '4px', color: msg.ok ? '#3b6d11' : '#c0392b' }}>{msg.text}</div>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
